/**
 * Docked, paged Clarification Sheet UI — port of WEB-CRM ClarificationSheet.tsx
 */

import { buildSheetAnswerMessage } from './rendering/clarification-sheet.js';
import { t } from '../../i18n.js';

const optionKey = (option) => option.value ?? option.label;
const BLANK_PAGE = { draft: '' };

const draftFor = (answer) => (answer?.kind === 'text' ? answer.text : '');
const liveAnswer = (state) =>
  state.answer && draftFor(state.answer) === state.draft.trim() ? state.answer : undefined;
const answerValue = (answer) => (answer.kind === 'pick' ? answer.option.label : answer.text);

/**
 * @param {Object} options
 * @param {Object} options.sheet
 * @param {function(string): void} options.onSend
 * @param {function(): void} [options.onDismiss]
 * @param {boolean} [options.disabled]
 * @returns {HTMLElement}
 */
export function createClarificationSheetUi({ sheet, onSend, onDismiss, disabled = false }) {
  const root = document.createElement('div');
  root.className = 'ai-clarification-sheet';
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', t('ai_sheet_label'));

  let pageIndex = 0;
  const pageStates = {};
  let dismissed = false;

  const setDisabled = (value) => {
    disabled = value;
    render();
  };

  function currentPage() {
    return sheet.pages[pageIndex];
  }

  function answeredEntries() {
    return sheet.pages
      .map((p) => ({ page: p, answer: liveAnswer(pageStates[p.field] ?? BLANK_PAGE) }))
      .filter((entry) => entry.answer);
  }

  function answerMessage() {
    return buildSheetAnswerMessage(
      answeredEntries().map(({ page, answer }) => ({
        fieldLabel: page.fieldLabel,
        value: answerValue(answer),
      }))
    );
  }

  function advance() {
    pageIndex = Math.min(pageIndex + 1, sheet.pages.length - 1);
    render();
  }

  function handlePick(option) {
    if (disabled) return;
    const page = currentPage();
    pageStates[page.field] = { draft: '', answer: { kind: 'pick', option } };
    advance();
  }

  function handleFreeTextSubmit() {
    if (disabled) return;
    const page = currentPage();
    const text = (pageStates[page.field]?.draft ?? '').trim();
    if (!text) return;
    pageStates[page.field] = { draft: text, answer: { kind: 'text', text } };
    advance();
  }

  function handleSkip() {
    if (disabled) return;
    advance();
  }

  function handleDismiss() {
    Object.keys(pageStates).forEach((k) => delete pageStates[k]);
    dismissed = true;
    root.replaceChildren();
    root.hidden = true;
    onDismiss?.();
  }

  function handleSend() {
    const message = answerMessage();
    if (disabled || !message) return;
    onSend(message);
  }

  function render() {
    if (dismissed) {
      root.replaceChildren();
      root.hidden = true;
      return;
    }

    const page = currentPage();
    if (!page) {
      root.replaceChildren();
      return;
    }

    const isLastPage = pageIndex === sheet.pages.length - 1;
    const pageState = pageStates[page.field] ?? BLANK_PAGE;
    const pageAnswer = liveAnswer(pageState);
    const questionText = page.question ?? page.fieldLabel;
    const answeredCount = answeredEntries().length;

    root.replaceChildren();
    root.hidden = false;

    const bar = document.createElement('div');
    bar.className = 'ai-clarification-sheet__bar';

    const pager = document.createElement('div');
    pager.className = 'ai-clarification-sheet__pager';

    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'ai-clarification-sheet__page-nav';
    prevBtn.textContent = '‹';
    prevBtn.setAttribute('aria-label', t('ai_sheet_previous'));
    prevBtn.disabled = pageIndex === 0;
    prevBtn.onclick = () => {
      pageIndex = Math.max(pageIndex - 1, 0);
      render();
    };

    const counter = document.createElement('span');
    counter.className = 'ai-clarification-sheet__counter';
    counter.setAttribute('aria-live', 'polite');
    counter.textContent = `${pageIndex + 1} / ${sheet.pages.length}`;

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'ai-clarification-sheet__page-nav';
    nextBtn.textContent = '›';
    nextBtn.setAttribute('aria-label', t('ai_sheet_next'));
    nextBtn.disabled = isLastPage;
    nextBtn.onclick = advance;

    pager.append(prevBtn, counter, nextBtn);

    const actions = document.createElement('div');
    actions.className = 'ai-clarification-sheet__actions';

    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className = 'ai-clarification-sheet__skip';
    skipBtn.textContent = t('ai_sheet_skip');
    skipBtn.disabled = disabled || isLastPage;
    skipBtn.onclick = handleSkip;

    const sendBtn = document.createElement('button');
    sendBtn.type = 'button';
    sendBtn.className = 'ai-clarification-sheet__send';
    sendBtn.textContent = t('ai_sheet_send');
    sendBtn.disabled = disabled || answeredCount === 0;
    sendBtn.onclick = handleSend;

    const dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.className = 'ai-clarification-sheet__dismiss';
    dismissBtn.textContent = '✕';
    dismissBtn.setAttribute('aria-label', t('ai_sheet_dismiss'));
    dismissBtn.onclick = handleDismiss;

    actions.append(skipBtn, sendBtn, dismissBtn);
    bar.append(pager, actions);

    const question = document.createElement('p');
    question.className = 'ai-clarification-sheet__question';
    question.textContent = questionText;

    const list = document.createElement('ol');
    list.className = 'ai-clarification-sheet__options';

    (page.options || []).forEach((option, i) => {
      const picked =
        pageAnswer?.kind === 'pick' && optionKey(pageAnswer.option) === optionKey(option);
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `ai-clarification-sheet__option${picked ? ' ai-clarification-sheet__option--picked' : ''}`;
      btn.disabled = disabled;
      btn.setAttribute('aria-pressed', picked ? 'true' : 'false');
      btn.onclick = () => handlePick(option);

      const index = document.createElement('span');
      index.className = 'ai-clarification-sheet__option-index';
      index.setAttribute('aria-hidden', 'true');
      index.textContent = String(i + 1);

      const label = document.createElement('span');
      label.className = 'ai-clarification-sheet__option-label';
      label.textContent = option.label;

      btn.append(index, label);
      li.appendChild(btn);
      list.appendChild(li);
    });

    const free = document.createElement('div');
    free.className = 'ai-clarification-sheet__free-text';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'ai-clarification-sheet__free-text-input';
    input.value = pageState.draft;
    input.placeholder = t('ai_sheet_free_text_placeholder');
    input.setAttribute('aria-label', t('ai_sheet_free_text_label'));
    input.disabled = disabled;
    input.oninput = () => {
      pageStates[page.field] = { ...(pageStates[page.field] || {}), draft: input.value };
      submit.disabled = disabled || !input.value.trim();
      sendBtn.disabled = disabled || answeredEntries().length === 0;
      const live = liveAnswer(pageStates[page.field] ?? BLANK_PAGE);
      list.querySelectorAll('.ai-clarification-sheet__option').forEach((btn, i) => {
        const option = page.options[i];
        const picked =
          live?.kind === 'pick' && optionKey(live.option) === optionKey(option);
        btn.classList.toggle('ai-clarification-sheet__option--picked', picked);
        btn.setAttribute('aria-pressed', picked ? 'true' : 'false');
      });
    };
    input.onkeydown = (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      handleFreeTextSubmit();
    };

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'ai-clarification-sheet__free-text-submit';
    submit.textContent = '↑';
    submit.setAttribute('aria-label', t('ai_sheet_free_text_submit'));
    submit.disabled = disabled || !pageState.draft.trim();
    submit.onclick = handleFreeTextSubmit;

    free.append(input, submit);
    root.append(bar, question, list, free);
  }

  render();
  root.setDisabled = setDisabled;
  return root;
}
